import * as React from "react";
import { theme } from "styles/theme";
import DragIcon from "material-ui/svg-icons/editor/drag-handle";
import {
    Item as ProductDisplayFieldPaths,
    NUMBER_FORMAT_CURRENCY_SYMBOL,
    TileViewSize,
    TILE_VIEW_ITEM_SIZES,
} from "constants/product-collection";
import { formatMessage } from "lib/globalize-util";
import LinkBrokenIcon from "components/icons/link-broken-icon";
import { ProductDisplayFields } from "../product-collection-item-modal/product-display-fields";
import {
    IDisplayFields,
    getInOrFormula,
    formatAssortmentPrice,
    getTileViewImageDimensions,
} from "helpers/product-collection";
import { getStyles } from "./product-cart-tile.styles";
import { ProductCollectionTileUnitary } from "./product-collection-tile-unitary";
import Popover from "material-ui/Popover";
import MoreHorizIcon from "material-ui/svg-icons/navigation/more-horiz";
import IconButton from "material-ui/IconButton";
import Menu from "material-ui/Menu";
import MenuItem from "material-ui/MenuItem";
import { IAddItem } from "actions/collections";
import * as uuid from "uuid/v4";
import { amphibiousGetIn, amphibiousToJS } from "helpers/objUtils";
import { ICollectionSwatch } from "constants/color-swatches";
import { ICurrency } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-renderers/pinned-row-renderers";
import { AssortmentShoppingActions } from "constants/analytics";
import me from "models/me";
import { WindowAnalytics } from "helpers/window-analytics";
import { ITileSettings, ITileSettingsValue } from "components/product-collection/manage-tile-view/types";
import { ProductCartTileTotals } from "./product-cart-tile-totals";
import { $, merge } from "glamor";

enum CONTEXT_MENU_VALUES {
    CLONE = "clone",
    CLONE_AS_SMU = "cloneAsSmu",
    REMOVE = "remove",
    ADD_PRODUCT = "addProduct",
    LINK = "linkProduct",
}

export interface IProductTileProps {
    brandEntities: any;
    cardViewFields: IDisplayFields;
    colorSwatch?: ICollectionSwatch;
    shopViewFields: IDisplayFields;
    data: any;
    editable: boolean;
    gridApi: any;
    imageUrl: string; // fallback url should be provided
    isSortable: boolean;
    productId?: string; // does not exist on catalogs
    secondaryImageUrl?: string;
    selected?: boolean;
    showLinking?: boolean; // only relevent for product tiles
    tertiaryAttributes?: string[];
    totalCost: number;
    totalDoors: number;
    totalUnits: number;
    totalRetail: number;
    style?: any; // only used in conjunction with react-virtualized
    dimensionFilters?: string[][];
    // primaryAttribute: string; // e.g. product name
    onAddItem: (params: IAddItem) => void;
    itemsId: string;
    onLinkProductToItem: (id: string) => void;
    collectionId: string;
    dataIndex: number;
    onOpenDeleteConfirmation: (itemsid, itemIndex) => void;
    allowDuplicateProduct: boolean;
    merchantCurrency?: ICurrency;
    tileSettings: ITileSettings;
    tileSettingsValue: ITileSettingsValue;
    tileViewSize: string;
    isServerSide?: boolean;
    openSizingGrid?: (lineItemId: string, refresh?: boolean) => void;
    onClickProductName?: (productId: string) => void;
    getProductNameLink?: (productId: string) => string;
}

const ProductCartTile: React.FunctionComponent<IProductTileProps> = ({
    isSortable,
    imageUrl,
    // primaryAttribute,
    // tertiaryAttributes = [],
    selected,
    style,
    secondaryImageUrl,
    colorSwatch,
    data,
    editable,
    productId,
    brandEntities,
    cardViewFields,
    shopViewFields,
    totalCost,
    totalDoors,
    totalUnits,
    totalRetail,
    onAddItem,
    itemsId,
    dataIndex,
    collectionId,
    onOpenDeleteConfirmation,
    onLinkProductToItem,
    dimensionFilters,
    allowDuplicateProduct,
    merchantCurrency,
    tileSettings,
    tileSettingsValue,
    tileViewSize,
    isServerSide,
    openSizingGrid,
    onClickProductName,
    getProductNameLink,
}) => {
    const [isMenuOpen, setIsMenuOpen] = React.useState<React.ReactInstance>();
    const tileDetailValues = React.useMemo(() => {
        if (!data) {
            return {};
        }

        const { is_cost, is_retail } = cardViewFields;
        const wholesaleValue = getInOrFormula(is_cost, data, isServerSide);
        const retailValue = getInOrFormula(is_retail, data, isServerSide);
        const wholesaleSymbol = is_cost && (is_cost.field.getIn(NUMBER_FORMAT_CURRENCY_SYMBOL) || "");
        const retailSymbol = is_retail && (is_retail.field.getIn(NUMBER_FORMAT_CURRENCY_SYMBOL) || "");
        const totalCostSymbol = merchantCurrency?.symbol || "";
        const totalRetailSymbol = merchantCurrency?.symbol || "";

        const totalCostDisplay = formatAssortmentPrice(totalCost, totalCostSymbol);
        const totalRetailDisplay = formatAssortmentPrice(totalRetail, totalRetailSymbol);
        const retailPriceDisplay = formatAssortmentPrice(retailValue, retailSymbol);
        const wholesalePriceDisplay = formatAssortmentPrice(wholesaleValue, wholesaleSymbol);

        return {
            brandName: tileSettingsValue[tileSettings.firstLine],
            productName: tileSettingsValue[tileSettings.secondLine],
            styleNumber: tileSettingsValue[tileSettings.thirdLine],
            color: tileSettingsValue[tileSettings.fourthLine],
            retailPrice: retailPriceDisplay,
            wholesalePrice: wholesalePriceDisplay,
            totalCost: totalCostDisplay,
            totalRetail: totalRetailDisplay,
        };
    }, [data, cardViewFields, totalUnits, totalCost, totalRetail, tileSettings, tileSettingsValue]); // eslint-disable-line react-hooks/exhaustive-deps

    const { width: tileViewImageWidth, height: tileViewImageHeight } = getTileViewImageDimensions(tileViewSize);
    const tileViewCardWidth = TILE_VIEW_ITEM_SIZES[tileViewSize].WIDTH;

    const styles = merge(
        $(" .node", {
            width: `${tileViewCardWidth}px !important`,
        }),
        $(" .images", {
            height: `${tileViewImageHeight}px !important`,
        }),
        $(" .image", {
            height: `${tileViewImageHeight}px !important`,
        }),
        $(" .primaryImage", {
            width: `${tileViewImageWidth}px !important`,
            height: `${tileViewImageHeight}px !important`,
            margin: "0 auto 0 auto !important",
        }),
        $(" .secondaryImageUrl", {
            width: `${tileViewImageWidth}px !important`,
            height: `${tileViewImageHeight}px !important`,
        }),
        $(" .noImage", {
            height: `${tileViewImageHeight}px !important`,
        }),
        $(" .cartTotals.hasTotalsAndOneUnit", {
            height:
                tileViewSize === TileViewSize.Small
                    ? "94px !important"
                    : tileViewSize === TileViewSize.Medium
                      ? "75px !important"
                      : "66px !important",
        }),
        $(" .cartTotals.hasOneTotalAndOneUnit", {
            marginTop: tileViewSize === TileViewSize.Small ? "30px" : "0",
        }),
        $(" .colorSwatch", {
            width: tileViewSize === TileViewSize.Small ? "17px !important" : "22px !important",
            height: tileViewSize === TileViewSize.Small ? "17px !important" : "22px !important",
            marginTop:
                tileViewSize === TileViewSize.Small
                    ? "2px !important"
                    : tileViewSize === TileViewSize.Medium
                      ? "0px !important"
                      : "1px !important",
        }),
        $(" .colorContainer .color", {
            fontSize: tileViewSize === TileViewSize.Small ? "9px !important" : "11px",
        }),
        $(" strong, span", {
            fontSize: tileViewSize === TileViewSize.Small ? "80% !important" : "100% !important",
        })
    );

    const contextMenu = React.useMemo(() => {
        return [
            allowDuplicateProduct
                ? {
                      text: formatMessage("Clone Item"),
                      value: CONTEXT_MENU_VALUES.CLONE,
                  }
                : null,
            {
                text: formatMessage("Clone Item as SMU"),
                value: CONTEXT_MENU_VALUES.CLONE_AS_SMU,
            },
            {
                text: formatMessage("Remove Item"),
                value: CONTEXT_MENU_VALUES.REMOVE,
            },
            {
                text: formatMessage("Link Product"),
                value: CONTEXT_MENU_VALUES.LINK,
            },
            allowDuplicateProduct
                ? {
                      text: formatMessage("Add Product Again"),
                      value: CONTEXT_MENU_VALUES.ADD_PRODUCT,
                  }
                : null,
        ].filter((item) => !!item);
    }, [allowDuplicateProduct]);

    const handleClosePopover = React.useCallback(() => {
        setIsMenuOpen(null);
    }, []);

    const handleClick = React.useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsMenuOpen(e.currentTarget);
    }, []);

    const handleContextAction = React.useCallback(
        ({ currentTarget }) => {
            const contextActionKey = currentTarget.getAttribute("data-value");
            const _data = amphibiousToJS(data);
            const dataFields = { ..._data.fields };

            switch (contextActionKey) {
                case CONTEXT_MENU_VALUES.CLONE:
                    onAddItem({
                        itemsId,
                        attributes: {
                            ..._data,
                            fields: {
                                ...dataFields,
                                id: { string_value: uuid() },
                            },
                        },
                        addType: "clone",
                        index: dataIndex + 1,
                        collectionId,
                    });
                    setIsMenuOpen(null);
                    break;
                case CONTEXT_MENU_VALUES.CLONE_AS_SMU:
                    delete dataFields._product_id;
                    delete dataFields._product_revision;
                    const attributes = {
                        ..._data,
                        fields: {
                            ...dataFields,
                            id: { string_value: uuid() },
                        },
                    };
                    const colorField = amphibiousToJS(shopViewFields?.is_color?.field);
                    if (colorField?.shop_view?.is_color) {
                        attributes.fields[colorField.field_name] = {
                            string_value: "blank",
                        };
                    }
                    onAddItem({
                        itemsId,
                        attributes,
                        addType: "cloneSMU",
                        index: dataIndex + 1,
                        collectionId,
                    });
                    WindowAnalytics.track(AssortmentShoppingActions.SmuCloned, {
                        userType: me.account_type,
                    });
                    setIsMenuOpen(null);
                    break;
                case CONTEXT_MENU_VALUES.REMOVE:
                    onOpenDeleteConfirmation(itemsId, [dataFields.id.string_value]);
                    setIsMenuOpen(null);
                    break;
                case CONTEXT_MENU_VALUES.ADD_PRODUCT:
                    onAddItem({
                        itemsId,
                        attributes: {
                            fields: {
                                id: { string_value: uuid() },
                                product_id: { string_value: productId },
                            },
                        },
                        index: dataIndex + 1,
                        productId,
                        collectionId,
                    });
                    setIsMenuOpen(null);
                    break;
                case CONTEXT_MENU_VALUES.LINK:
                    onLinkProductToItem(dataFields.id.string_value);
                    setIsMenuOpen(null);
                    break;
            }
        },
        [
            onAddItem,
            itemsId,
            collectionId,
            data,
            dataIndex,
            onOpenDeleteConfirmation,
            onLinkProductToItem,
            productId,
            shopViewFields,
        ]
    );

    const handleItemClicked = React.useCallback(() => {
        if (openSizingGrid) {
            const lineItemId = amphibiousGetIn(data, ProductDisplayFieldPaths.FIELDS.LINE_ITEM_ID);
            openSizingGrid(lineItemId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openSizingGrid]);

    const showImageBlock = React.useMemo(
        () => !tileSettings?.viewDef || tileSettings?.viewDef?.showImage,
        [tileSettings]
    );

    return (
        <div
            {...getStyles()}
            className={selected ? "selected" : undefined}
            style={style}
            onClick={handleItemClicked}
            {...styles}
        >
            <div className="node">
                {isSortable && (
                    <div className="dragHandle" data-draghandle={true}>
                        <DragIcon color={theme.palette.grey} />
                    </div>
                )}
                {editable && (
                    <IconButton className="moreActions" onClick={handleClick} data-stoppropagation={true}>
                        <MoreHorizIcon color={theme.palette.darkGrey} data-stoppropagation={true} />
                    </IconButton>
                )}
                <Popover
                    open={!!isMenuOpen}
                    anchorEl={isMenuOpen}
                    onRequestClose={handleClosePopover}
                    anchorOrigin={{ horizontal: "left", vertical: "top" }}
                >
                    <Menu>
                        {contextMenu.map((contextMenuEntry) => {
                            if (contextMenuEntry.value === CONTEXT_MENU_VALUES.ADD_PRODUCT && !productId) {
                                return null;
                            }
                            return (
                                <MenuItem
                                    key={contextMenuEntry.value}
                                    data-value={contextMenuEntry.value}
                                    primaryText={contextMenuEntry.text}
                                    onClick={handleContextAction}
                                />
                            );
                        })}
                    </Menu>
                </Popover>
                {showImageBlock && imageUrl && (
                    <div className="images">
                        <div
                            className="primaryImage"
                            style={{
                                width: `${tileViewImageWidth}px`,
                                height: `${tileViewImageHeight}px`,
                                backgroundImage: `url(${imageUrl}?max_width=${tileViewImageWidth})`,
                                backgroundSize: "contain",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "center center",
                            }}
                        />
                        {secondaryImageUrl && (
                            <div
                                className="secondaryImageUrl"
                                style={{
                                    width: `${tileViewImageWidth}px`,
                                    height: `${tileViewImageHeight}px`,
                                    backgroundImage: `url(${secondaryImageUrl}?max_width=${tileViewImageWidth})`,
                                    backgroundSize: "contain",
                                    backgroundRepeat: "no-repeat",
                                    backgroundPosition: "center center",
                                    backgroundColor: "white",
                                }}
                            />
                        )}
                    </div>
                )}
                {showImageBlock && !imageUrl && <div className="image" onClick={handleItemClicked} />}
                <ProductDisplayFields
                    productName={tileDetailValues.productName}
                    styleNumber={tileDetailValues.styleNumber}
                    brandId={amphibiousGetIn(data, ProductDisplayFieldPaths.FIELDS.BRAND_ID)}
                    productId={amphibiousGetIn(data, ProductDisplayFieldPaths.FIELDS.PRODUCT_ID)}
                    colorSwatch={colorSwatch}
                    hidePrices={true}
                    brandEntities={brandEntities}
                    brandNameOverride={tileDetailValues.brandName}
                    tileSettings={tileSettings}
                    tileSettingsValue={tileSettingsValue}
                    color={tileDetailValues.color}
                    onClickProductName={onClickProductName}
                    getProductNameLink={getProductNameLink}
                />
                {(tileSettings.showTotalCost ||
                    tileSettings.showTotalRetail ||
                    tileSettings.showUnits ||
                    tileSettings.showDoors) && (
                    <ProductCartTileTotals
                        totalUnits={totalUnits}
                        totalDoors={totalDoors}
                        totalRetail={tileDetailValues.totalRetail}
                        totalCost={tileDetailValues.totalCost}
                        wholesalePrice={tileDetailValues.wholesalePrice}
                        retailPrice={tileDetailValues.retailPrice}
                        tileSettings={tileSettings}
                        tileSettingsValue={tileSettingsValue}
                        dimensionFilters={dimensionFilters}
                    />
                )}
                {/* Broken link applies only to collections. Not to legacy cart */}
                {!productId && collectionId && (
                    <LinkBrokenIcon className="brokenLink" fill={theme.palette.lighterGrey} />
                )}

                {totalUnits > 0 && tileSettings.showEachesOnHover && (
                    <div className="node-unitaryDetails">
                        <ProductCollectionTileUnitary
                            tileSettings={tileSettings}
                            costPrice={tileDetailValues.wholesalePrice}
                            retailPrice={tileDetailValues.retailPrice}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

// separate export statement so function name shows up in React DevTools
export { ProductCartTile };
